// Supported field widgets for the library form builder.
// 'text' maps to antd-form-builder's default input (no `widget` key emitted).
// Others set `widget` to the value. Domain values live here (code-standards F4).

// NOTE: option-based widgets MUST use antd-form-builder's group widget names
// ('radio-group' / 'checkbox-group') — the singular 'radio'/'checkbox' render a
// single control and IGNORE `options` (antd-form-builder README).
export const LIBRARY_FIELD_WIDGETS = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'number', label: 'Number' },
  { value: 'select', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox (single)' },
  { value: 'checkbox-group', label: 'Checkbox Group' },
  { value: 'radio', label: 'Radio (single)' },
  { value: 'radio-group', label: 'Radio Group' },
  { value: 'switch', label: 'Switch (yes/no)' },
  { value: 'date-picker', label: 'Date' },
  { value: 'scale', label: 'Scale (rating)' },
  { value: 'signature', label: 'Signature' },
  { value: 'location', label: 'Location (map pin)' },
  { value: 'upload_file', label: 'Upload File' },
  { value: 'host-picker', label: 'List Hosts' },
  // An autocomplete over the property's Areas that still accepts typed text —
  // a contractor can be sent somewhere that was never drawn on the map.
  { value: 'area-picker', label: 'Property Area / Location' },
  { value: 'visitor-types', label: 'Visitor Types' },
  { value: 'fixed_text', label: 'Fixed Text (display)' },
  { value: 'fixed_image', label: 'Fixed Image (display)' },
  // A document to read while filling the form — a safety data sheet, a site
  // map, a policy. Displays like Fixed Image, but a PDF is opened and
  // downloaded rather than shown inline.
  { value: 'fixed_pdf', label: 'Fixed PDF (display)' },
];

// Widgets that take a list of options (options are required for these).
// NOTE: 'visitor-types' is NOT here — it carries `{ value, label }` option objects
// (from the visitorTypes constant), configured via its own dedicated editor, not
// the free-text options list.
export const WIDGETS_WITH_OPTIONS = ['select', 'checkbox-group', 'radio-group'];

// Static display widgets that carry a `content` (text or image URL) and collect no value.
export const WIDGETS_WITH_CONTENT = ['fixed_text', 'fixed_image', 'fixed_pdf'];

// The set of valid widget values (for validation / render fallback).
export const LIBRARY_FIELD_WIDGET_VALUES = LIBRARY_FIELD_WIDGETS.map((w) => w.value);

export const DEFAULT_WIDGET = 'text';

export const getWidgetLabel = (value) => {
  const option = LIBRARY_FIELD_WIDGETS.find((opt) => opt.value === value);
  if (option) {
    return option.label;
  }
  // Legacy singular names from older/imported data.
  if (value === 'radio') {
    return 'Radio';
  }
  if (value === 'checkbox') {
    return 'Checkbox';
  }
  return value || 'Text';
};

// Pick the editor Select value for an existing field. Only a legacy singular
// checkbox/radio that CARRIES OPTIONS is treated as a group; a genuine single
// checkbox/radio (no options) stays single.
export const normalizeWidget = (widget, hasOptions) => {
  if (hasOptions && widget === 'radio') {
    return 'radio-group';
  }
  if (hasOptions && widget === 'checkbox') {
    return 'checkbox-group';
  }
  return widget || DEFAULT_WIDGET;
};

// Widgets that don't stretch to fill their column by default (unlike Input) and
// need an explicit width:100% to fill it — applied at render time.
const FULL_WIDTH_WIDGETS = ['select', 'date-picker', 'time-picker', 'number', 'host-picker', 'area-picker'];

// Signature detection: the signature widget, or (legacy imported forms) a field
// keyed exactly 'signature' whose widget is a plain text/empty — those AI-generated
// forms put the intent in the key, not the widget. Mirrors SchemaFormService::isSignatureField.
export const isSignatureField = (field) => {
  if (!field) {
    return false;
  }
  if (field.widget === 'signature') {
    return true;
  }
  const key = String(field.key || '').trim().toLowerCase();
  return key === 'signature' && (!field.widget || field.widget === 'text' || field.widget === 'textarea');
};

// Normalizer for RENDERING (preview/runtime). Two jobs:
//  1. Options-aware widget upgrade: legacy singular radio/checkbox WITH options
//     -> their group widget, so a genuine single checkbox stays single.
//  2. Force full column width on block widgets (DatePicker/Select/InputNumber/…)
//     via widgetProps.style, unless a width is already set.
// Applied at render only — never mutates the saved schema.
export const normalizeFieldWidget = (field) => {
  if (!field) {
    return field;
  }
  let result = field;

  // Safety net: coerce any unsupported widget to 'text' so the renderer can't
  // crash on an unknown widget (mirrors the import-time validation).
  if (result.widget && LIBRARY_FIELD_WIDGET_VALUES.indexOf(result.widget) === -1) {
    result = { ...result, widget: 'text' };
  }

  // Visitor Types is a plain dropdown seeded with visitor-type labels — render it
  // as a real select of its (author-editable) options.
  if (result.widget === 'visitor-types') {
    result = { ...result, widget: 'select' };
  }

  // Legacy imported signature: AI-generated forms store the signature as a plain
  // text field keyed 'signature'. Render it as a real signature widget.
  if (isSignatureField(result) && result.widget !== 'signature') {
    result = { ...result, widget: 'signature' };
  }

  const hasOptions = result.options && result.options.length;

  // Legacy singular checkbox/radio that carries options -> its group widget
  // (label on top, options below).
  if (hasOptions && result.widget === 'radio') {
    result = { ...result, widget: 'radio-group' };
  } else if (hasOptions && result.widget === 'checkbox') {
    result = { ...result, widget: 'checkbox-group' };
  }

  // Single checkbox/radio (no options): render the label INLINE beside the
  // control as the widget's text. antd-form-builder reads the widget text from
  // the field-level `children` meta (not widgetProps); drop the top label.
  if (!hasOptions && (result.widget === 'checkbox' || result.widget === 'radio')) {
    const { label, ...rest } = result;
    result = { ...rest, children: label };
  }

  // List Hosts with a curated set (hostOptions) — render as a real select of the
  // chosen hosts' labels in preview (the runtime does the same via property data).
  // Without hostOptions it stays 'host-picker' → the disabled placeholder widget.
  if (result.widget === 'host-picker' && Array.isArray(result.hostOptions) && result.hostOptions.length) {
    const { hostOptions, ...rest } = result;
    result = { ...rest, widget: 'select', options: hostOptions.map((o) => ({ value: o.userId, label: o.label })) };
  }

  // Static display widgets: pass the configured content to the widget AND drop the
  // field label — a Fixed Text / Fixed Image block shows only its content, never the
  // author's internal label. (The builder still shows the label in its config table.)
  // They collect no value, so they must NEVER be required or carry validation rules —
  // otherwise validateFields() reports "Please enter <key>" for a display-only block.
  if (result.widget === 'fixed_text' || result.widget === 'fixed_image' || result.widget === 'fixed_pdf') {
    const { label, required, rules, ...rest } = result;
    result = rest;
    if (result.content !== undefined) {
      result = { ...result, widgetProps: { ...result.widgetProps, content: result.content } };
    }
    // A PDF keeps its label and filename: unlike an image, a document that
    // shows only a URL tells the reader nothing about what they are opening.
    if (result.widget === 'fixed_pdf') {
      result = {
        ...result,
        widgetProps: {
          ...result.widgetProps,
          contentName: result.contentName,
          contentLabel: label,
        },
      };
    }
  } else if (result.rules && result.rules.length) {
    result = { ...result, rules: normalizeFieldRules(result.rules, result.widget) };
  }

  // Scale: pass the number of points as `count`.
  if (result.widget === 'scale') {
    result = { ...result, widgetProps: { ...result.widgetProps, count: result.max || 5 } };
  }

  if (FULL_WIDTH_WIDGETS.indexOf(result.widget) !== -1) {
    const existingStyle = (result.widgetProps && result.widgetProps.style) || {};
    if (existingStyle.width === undefined) {
      result = {
        ...result,
        widgetProps: { ...result.widgetProps, style: { width: '100%', ...existingStyle } },
      };
    }
  }

  return result;
};

/**
 * Insert a full-width horizontal rule after every field the author marked with
 * "Add horizontal line at end" (`dividerAfter`).
 *
 * A real ITEM in the field list rather than a border under the field, because
 * the line is meant to close off a section: in a 2-column step a border would
 * underline one column and stop halfway across, while a colSpan item spans the
 * row and pushes the next field onto a fresh one — which is what a section
 * break should do.
 *
 * Takes fields that are ALREADY mapped for render, so the divider it inserts is
 * final and never goes back through normalizeFieldWidget (which would coerce an
 * unrecognised widget to 'text' and put an empty input on the page).
 *
 * Carries no value and no rules: `key` is suffixed so it cannot collide with a
 * real field, and every renderer treats `divider` as display-only.
 *
 * Returns the SAME array when nothing is marked, so it is free on the forms
 * that do not use it.
 *
 * @param {Array}  fields  render-ready fields
 * @param {number} columns the step's column count
 */
export const withRowDividers = (fields, columns) => {
  if (!Array.isArray(fields) || !fields.some((f) => f && f.dividerAfter)) {
    return fields;
  }
  const out = [];
  fields.forEach((f) => {
    const { dividerAfter, ...rest } = f || {};
    out.push(rest);
    if (dividerAfter) {
      // No `label` key at all — not `label: null`. antd-form-builder renders a
      // Form.Item label element for any label that is present, so a null one
      // leaves an empty line above the rule. Same reason fixed_text deletes
      // its label rather than blanking it.
      out.push({
        key: `${rest.key || out.length}__hr`,
        widget: 'divider',
        colSpan: Math.max(1, Number(columns) || 1),
      });
    }
  });
  return out;
};

// Bake the property's hosts (Manage Hosts data: [{ user_id, name, host_display }])
// into any "List Hosts" (host-picker) field that has no explicit selection, so an
// UNCONFIGURED field defaults to showing ALL hosts. Applied when a per-property
// (standalone) form is saved, so the schema is self-contained at runtime (incl.
// the public visitor form, which cannot look hosts up on its own). Fields the
// author already curated (non-empty hostOptions) are left untouched. Returns the
// SAME reference when nothing changed, so it is safe inside a reconcile effect.
export const bakeHostDefaults = (schema, admins) => {
  if (!schema || !Array.isArray(schema.steps) || !Array.isArray(admins) || !admins.length) {
    return schema;
  }
  let changed = false;
  const steps = schema.steps.map((step) => {
    const meta = step.formMeta || {};
    const fields = (meta.fields || []).map((f) => {
      if (f && f.widget === 'host-picker' && !(Array.isArray(f.hostOptions) && f.hostOptions.length)) {
        changed = true;
        return {
          ...f,
          hostOptions: admins.map((a) => ({ userId: Number(a.user_id), label: a.host_display || a.name || '' })),
        };
      }
      return f;
    });
    return { ...step, formMeta: { ...meta, fields } };
  });
  return changed ? { ...schema, steps } : schema;
};

// Turn a label into a safe field key (dot-free camelCase-ish slug).
export const slugifyKey = (label) => {
  if (!label) {
    return '';
  }
  return String(label)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
};

// ---- antd-form-builder `clear` meta (2-column row layout) ----
// In a 2-column step, `clear` forces the rest of the row empty so the next
// field wraps to a new row: 'left' clears the left cell, 'right' the right cell.
export const FIELD_CLEAR_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'left', label: 'Clear left (field starts a new row)' },
  { value: 'right', label: 'Clear right (rest of row empty)' },
  { value: 'both', label: 'Clear both' },
];

// ---- antd Form validation `rules` ----
// A field's `rules` is an array of antd rule objects. We model each rule in the
// UI as { kind, value, message } and convert to/from the antd shape.
export const RULE_KINDS = [
  { value: 'type', label: 'Format (type)' },
  { value: 'pattern', label: 'Pattern (regex)' },
  { value: 'min', label: 'Min length/value' },
  { value: 'max', label: 'Max length/value' },
  { value: 'len', label: 'Exact length' },
  { value: 'whitespace', label: 'No whitespace' },
];

export const RULE_TYPE_OPTIONS = [
  { value: 'string', label: 'String' },
  { value: 'email', label: 'Email' },
  { value: 'url', label: 'URL' },
  { value: 'number', label: 'Number' },
  { value: 'integer', label: 'Integer' },
  { value: 'date', label: 'Date' },
];

const NUMERIC_KINDS = ['min', 'max', 'len'];
const RANGE_RULE_KEYS = ['min', 'max', 'len'];

// ---- Field-type–aware validation ----
// Not every validation rule makes sense for every field type. A checkbox/switch
// is a boolean, a dropdown is a fixed choice, a signature is a blob — for those the
// only meaningful validation is `required` (handled by its own switch). Free-text
// fields accept string-shaped rules; number/scale accept numeric-shaped rules.
// These helpers drive which rule controls the FieldEditorModal shows per widget.
export const FREE_TEXT_WIDGETS = ['text', 'textarea'];
// Scale is a bounded rating (1..max) chosen by tapping stars — min/max/format
// rules are redundant, so it takes no validation rules (Required only).
export const NUMERIC_INPUT_WIDGETS = ['number'];

// The validation-rule KINDS (RULE_KINDS values) that apply to a given widget.
// Empty array => the widget takes no validation rules (only `required`).
export const getAllowedRuleKinds = (widget) => {
  const w = widget || DEFAULT_WIDGET;
  if (FREE_TEXT_WIDGETS.indexOf(w) !== -1) {
    return ['type', 'pattern', 'min', 'max', 'len', 'whitespace'];
  }
  if (NUMERIC_INPUT_WIDGETS.indexOf(w) !== -1) {
    return ['type', 'min', 'max'];
  }
  // boolean (switch/checkbox/radio), choice (select/*-group/visitor-types/host-picker),
  // date, file (signature/upload_file) and display widgets: only `required` applies.
  return [];
};

// The `type`-kind sub-options (RULE_TYPE_OPTIONS values) that apply to a widget.
export const getAllowedRuleTypeOptions = (widget) => {
  const w = widget || DEFAULT_WIDGET;
  if (FREE_TEXT_WIDGETS.indexOf(w) !== -1) {
    return RULE_TYPE_OPTIONS.filter((o) => ['string', 'email', 'url'].indexOf(o.value) !== -1);
  }
  if (NUMERIC_INPUT_WIDGETS.indexOf(w) !== -1) {
    return RULE_TYPE_OPTIONS.filter((o) => ['number', 'integer'].indexOf(o.value) !== -1);
  }
  return [];
};

// Does this widget accept ANY validation rule beyond `required`?
export const widgetSupportsValidationRules = (widget) =>
  getAllowedRuleKinds(widget).length > 0;

// Drop rules whose kind is not allowed for the widget, and clear a `type` value
// that is no longer a valid option — used when the field's type changes.
export const pruneRulesForWidget = (rules, widget) => {
  const allowedKinds = getAllowedRuleKinds(widget);
  if (!allowedKinds.length) {
    return [];
  }
  const allowedTypeValues = getAllowedRuleTypeOptions(widget).map((o) => o.value);
  return (rules || [])
    .filter((r) => r && allowedKinds.indexOf(r.kind) !== -1)
    .map((r) => (
      r.kind === 'type' && allowedTypeValues.indexOf(r.value) === -1
        ? { ...r, value: undefined }
        : r
    ));
};

const hasRangeConstraint = (rule) =>
  RANGE_RULE_KEYS.some((key) => rule && rule[key] !== undefined);

const inferRangeRuleType = (widget) => {
  const w = widget || DEFAULT_WIDGET;
  if (w === 'number' || w === 'scale') {
    return 'number';
  }
  if (w === 'text' || w === 'textarea') {
    return 'string';
  }
  return null;
};

// async-validator defaults min/max/len rules to type "string". Number inputs
// (InputNumber) store numeric values, so both min and max rules fail and show
// their custom messages even for valid values like 10. Attach the right type.
export const normalizeFieldRules = (rules, widget) => {
  if (!rules || !rules.length) {
    return rules;
  }
  const rangeType = inferRangeRuleType(widget);
  if (!rangeType) {
    return rules;
  }
  let changed = false;
  const normalized = rules.map((rule) => {
    if (!hasRangeConstraint(rule)) {
      return rule;
    }
    // A text field with type:number on a min/max rule cannot work — Input
    // values are strings, so coerce back to string (length) validation.
    const type = rule.type === undefined
      ? rangeType
      : (rangeType === 'string' && rule.type === 'number' ? 'string' : rule.type);
    if (type === rule.type) {
      return rule;
    }
    changed = true;
    return { ...rule, type };
  });
  return changed ? normalized : rules;
};

// antd rules array -> UI state [{ kind, value, message }]
export const parseRulesToState = (rules) => (rules || []).map((rule) => {
  if (rule.type !== undefined) {
    return { kind: 'type', value: rule.type, message: rule.message || '' };
  }
  if (rule.pattern !== undefined) {
    return { kind: 'pattern', value: rule.pattern, message: rule.message || '' };
  }
  if (rule.min !== undefined) {
    return { kind: 'min', value: rule.min, message: rule.message || '' };
  }
  if (rule.max !== undefined) {
    return { kind: 'max', value: rule.max, message: rule.message || '' };
  }
  if (rule.len !== undefined) {
    return { kind: 'len', value: rule.len, message: rule.message || '' };
  }
  if (rule.whitespace) {
    return { kind: 'whitespace', value: undefined, message: rule.message || '' };
  }
  return { kind: 'type', value: undefined, message: rule.message || '' };
});

// ---- Conditional field visibility ----
// A field may carry `visibleWhen: { field, operator, value }` — it is shown only
// when the referenced (earlier) field's answer satisfies the condition. `field`
// is another field's KEY; `operator` is one of VISIBILITY_OPERATORS; `value` is
// compared for eq/ne and ignored for filled/empty. Evaluated identically in the
// preview + runtime renderers and mirrored server-side in SchemaFormService.
export const VISIBILITY_OPERATORS = [
  { value: 'eq', label: 'Equals' },
  { value: 'ne', label: 'Does not equal' },
  { value: 'filled', label: 'Is answered' },
  { value: 'empty', label: 'Is empty' },
];

// Operators that compare against a `value` (the others test only answered/empty).
export const VISIBILITY_OPERATORS_WITH_VALUE = ['eq', 'ne'];

const isAnswerEmpty = (value) =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

// True when a raw answer satisfies a visibleWhen condition. A missing controlling
// answer is treated as empty (so an `eq` condition on an unanswered field hides
// the dependent field). `value` may be a single value OR a list (e.g. a Visitor
// Types condition — "is one of"): eq matches when the answer is ANY of the listed
// values, ne when it is NONE. Multi-value answers (checkbox-group) match by
// overlap; booleans (switch) compare against "true"/"false".
export const evalVisibleWhen = (cond, values) => {
  if (!cond || !cond.field) {
    return true;
  }
  const actual = values ? values[cond.field] : undefined;
  const empty = isAnswerEmpty(actual);
  const expected = Array.isArray(cond.value) ? cond.value.map(String) : [String(cond.value)];
  const matchesAny = Array.isArray(actual)
    ? actual.map(String).some((a) => expected.indexOf(a) !== -1)
    : expected.indexOf(String(actual)) !== -1;
  switch (cond.operator) {
    case 'filled':
      return !empty;
    case 'empty':
      return empty;
    case 'ne':
      return !matchesAny;
    case 'eq':
    default:
      return matchesAny;
  }
};

// Whether a field is visible given the current form values (no condition = always).
export const isFieldVisible = (field, values) =>
  !field || !field.visibleWhen || evalVisibleWhen(field.visibleWhen, values);

const withMessage = (rule, message) => (message ? { ...rule, message } : rule);

// UI state -> antd rules array (drops incomplete rules)
export const buildRulesFromState = (state, widget) => {
  const built = (state || [])
    .map((item) => {
      if (item.kind === 'whitespace') {
        return withMessage({ whitespace: true }, item.message);
      }
      if (item.kind === 'type' && item.value) {
        return withMessage({ type: item.value }, item.message);
      }
      if (item.kind === 'pattern' && item.value) {
        return withMessage({ pattern: item.value }, item.message);
      }
      if (NUMERIC_KINDS.indexOf(item.kind) !== -1 && item.value !== undefined && item.value !== '' && !isNaN(Number(item.value))) {
        return withMessage({ [item.kind]: Number(item.value) }, item.message);
      }
      return null;
    })
    .filter(Boolean);
  return normalizeFieldRules(built, widget) || built;
};
